// @flow

import { all, call, put, takeEvery } from "redux-saga/effects";
import type { Action } from "redux-action";
import moment from "moment";
import http from "@http";
import config from "@config";
import actionCreators from "@modules/reporting/actionCreator";
import {
  GetSupplierClosedReferrals,
  GetSupplierClosedReferralsStatuses,
  GetSupplierClosedReferralsExport,
  GetRTWReferralDetails,
  GetLogReferralDetails,
  SubmitLogReferralData,
} from "@modules/reporting/constants/actions";
import { SUPPLIER_REFERRAL_REPORT_TYPE, SUPPLIER_REFERRAL_STATUSES } from "../../constants/reports";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import type { ClosedReferralsFilters } from "@modules/reporting/reducer/referral-tracker/types";
import { downloadCsvFile } from "@sagas/utils";

const URL_PATH = (clientId, page) =>
  `/api/v1/reports/client/${clientId}/reports/supplier-referral-tasks-report?limit=${
    config.shortResultsPerPage
  }&page=${page || 1}`;

type ReferralsRequest = $Exact<{
  clientId: number,
  page?: number,
  filters?: ClosedReferralsFilters,
}>;

export function* onGetSupplierClosedReferrals({
  payload,
}: Action<ReferralsRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId) {
    throw Error("Invalid payload provided");
  }

  try {
    const response = yield call(http.post, URL_PATH(payload.clientId, payload.page), {
      supplier_referral_tasks_report_type: SUPPLIER_REFERRAL_REPORT_TYPE.CLOSED_REFERRALS,
      status:
        (payload.filters && payload.filters.status && payload.filters.status) ||
        SUPPLIER_REFERRAL_STATUSES.ALL,
      from_date: (payload.filters && payload.filters.fromDate) || null,
      to_date: (payload.filters && payload.filters.toDate) || null,
    });

    yield put(actionCreators.GetSupplierClosedReferralsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetSupplierClosedReferralsError(e.response));
  }
}

type ReferralDetailsRequest = $Exact<{
  clientId: number,
  supplierReferralId: number,
  referralType: string,
}>;

//RTW
export function* onGetRTWReferralDetails({
  payload,
}: Action<ReferralDetailsRequest>): Generator<*, void, *> {
  if (!payload) {
    throw Error("Invalid payload provided");
  }

  try {
    const response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/reports/referral-details/supplier/task/${payload.supplierReferralId}/rtw-and-fit-date`,
    );

    yield all([
      put(actionCreators.GetRTWReferralDetailsSuccess(response.data)),
      put(actionCreators.ToggleRTWReferralDetailsModal()),
    ]);
  } catch (e) {
    yield put(actionCreators.GetRTWReferralDetailsError(e.response));
  }
}
// Log
type ReferralLogDetailsRequest = $Exact<{
  clientId: number,
  supplierReferralId: number,
}>;
export function* onGetLogReferralDetails({
  payload,
}: Action<ReferralLogDetailsRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.supplierReferralId) {
    throw Error("Invalid payload provided");
  }

  try {
    const response = yield call(
      http.get,
      `api/v1/reports/client/${payload.clientId}/reports/referral-details/supplier/task/${payload.supplierReferralId}/get-treatment-logs`,
    );
    yield getOutcomeOptions({
      type: "",
      payload: {
        clientId: payload.clientId,
        supplierReferralId: payload.supplierReferralId,
      },
    });

    yield all([
      put(actionCreators.GetLogReferralDetailsSuccess(response.data)),
      put(actionCreators.ToggleLogReferralDetailsModal()),
    ]);
  } catch (e) {
    yield put(actionCreators.GetLogReferralDetailsError(e.response));
  }
}
type ReferralLogPostRequest = $Exact<{
  clientId: number,
  supplierReferralId: number,
  requestBody: Object,
}>;
export function* onSubmitLogReferralData({
  payload,
}: Action<ReferralLogPostRequest>): Generator<*, void, *> {
  const { clientId, supplierReferralId, requestBody } = payload || {};
  if (!clientId || !supplierReferralId || !requestBody) {
    throw Error("Invalid payload provided");
  }

  try {
    const response = yield call(
      http.post,
      `api/v1/reports/client/${clientId}/reports/referral-details/supplier/task/${supplierReferralId}/add-treatment-log`,
      { ...requestBody },
    );

    yield all([put(actionCreators.SubmitLogReferralDataSuccess(response.data))]);
  } catch (e) {
    yield put(actionCreators.SubmitLogReferralDataError(e.response));
  }
}

export function* getOutcomeOptions({
  payload,
}: Action<ReferralLogDetailsRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.supplierReferralId) {
    throw Error("Invalid payload provided");
  }

  try {
    const response = yield call(
      http.get,
      `api/v1/reports/client/${payload.clientId}/reports/referral-details/supplier/task/${payload.supplierReferralId}/get-outcome-options`,
    );
    yield all([
      put(
        actionCreators.SetReferralCommonData({
          outcomeOptions: response.data,
          supplierReferralId: payload.supplierReferralId,
          clientId: payload.clientId,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.GetLogReferralOutcomeOptionsError(e.response));
  }
}
// Status

type ClosedReferralStatusesRequest = $Exact<{
  clientId: number,
}>;

export function* onGetSupplierClosedReferralsStatuses({
  payload,
}: Action<ClosedReferralStatusesRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId) {
    throw Error("Invalid payload provided");
  }

  try {
    const response = yield call(
      http.get,
      `/api/v1/tools/client/${payload.clientId}/tools/task/supplier-referral-tasks/statuses`,
    );

    yield all([put(actionCreators.GetSupplierClosedReferralsStatusesSuccess(response.data))]);
  } catch (e) {
    yield put(actionCreators.GetSupplierClosedReferralsStatusesError(e.response));
  }
}

export function* onGetSupplierClosedReferralsExport({
  payload,
}: Action<ReferralsRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId) {
    throw Error("Invalid payload provided");
  }

  try {
    const todaysDate = moment().format("YYYY-MM-DD");
    const response = yield call(
      http.post,
      `/api/v1/reports/client/${payload.clientId}/reports/supplier-referral-tasks-report/export`,
      {
        supplier_referral_tasks_report_type: SUPPLIER_REFERRAL_REPORT_TYPE.CLOSED_REFERRALS,
        status:
          (payload.filters && payload.filters.status && payload.filters.status) ||
          SUPPLIER_REFERRAL_STATUSES.ALL,
        from_date: (payload.filters && payload.filters.fromDate) || null,
        to_date: (payload.filters && payload.filters.toDate) || null,
      },
    );

    downloadCsvFile(
      response.data,
      `${(payload.filters && payload.filters.fromDate) || todaysDate}_${(payload.filters &&
        payload.filters.toDate) ||
        todaysDate}_closed_supplier_referral_export_.csv`,
    );

    yield all([put(actionCreators.GetSupplierClosedReferralsExportSuccess(response.data))]);
  } catch (e) {
    yield put(actionCreators.GetSupplierClosedReferralsExportError(e.response));
  }
}

export function* ReferralsTrackerSaga(): Generator<*, void, void> {
  yield all([takeEvery(GetSupplierClosedReferrals, onGetSupplierClosedReferrals)]);
  yield all([takeEvery(GetRTWReferralDetails, onGetRTWReferralDetails)]);
  yield all([takeEvery(GetLogReferralDetails, onGetLogReferralDetails)]);
  yield all([takeEvery(SubmitLogReferralData, onSubmitLogReferralData)]);
  yield all([takeEvery(GetSupplierClosedReferralsStatuses, onGetSupplierClosedReferralsStatuses)]);
  yield all([takeEvery(GetSupplierClosedReferralsExport, onGetSupplierClosedReferralsExport)]);
}

export default ReferralsTrackerSaga;
